
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { startOfDay, addDays, endOfDay } from "date-fns";
import { fetchUnifiedFlights } from "@/services/flights/unifiedFlightQuery";
import { FIDSHeader } from "@/components/fids/FIDSHeader";
import { FIDSMarquee } from "@/components/fids/FIDSMarquee";
import { FlightTabs } from "@/components/FlightTabs";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { FlightDetailPanel } from "@/components/fids/FlightDetailPanel";
import { useRealtimeFlights } from "@/hooks/useRealtimeFlights";
import { Loader2 } from "lucide-react";
import { OrganizationLinks } from "@/components/OrganizationLinks";
import { Flight } from "@/types/flight";

export default function FlightBoardPage() {
  useRealtimeFlights();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: flights = [], isLoading } = useQuery({
    queryKey: ['flights'],
    queryFn: async () => {
      const today = startOfDay(new Date());
      const dayAfterTomorrow = startOfDay(addDays(new Date(), 2));
      return fetchUnifiedFlights({
        gteDate: today.toISOString(),
        ltDate: dayAfterTomorrow.toISOString(),
      });
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const todaysFlights = useMemo(() => {
    const today = startOfDay(new Date());
    const endOfTomorrow = endOfDay(addDays(new Date(), 1));
    return flights.filter(flight => {
      const dep = new Date(flight.scheduled_departure);
      return dep >= today && dep <= endOfTomorrow;
    });
  }, [flights]);

  const filteredFlights = useMemo(() => {
    if (!searchTerm.trim()) return todaysFlights;
    const term = searchTerm.trim().toLowerCase();
    return todaysFlights.filter(f =>
      f.flight_number?.toLowerCase().includes(term) ||
      f.departure_city?.name?.toLowerCase().includes(term) ||
      f.departure_city?.code?.toLowerCase().includes(term) ||
      f.arrival_city?.name?.toLowerCase().includes(term) ||
      f.arrival_city?.code?.toLowerCase().includes(term)
    );
  }, [todaysFlights, searchTerm]);

  // Auto-open panel from URL param
  useEffect(() => {
    const flightParam = searchParams.get("flight");
    if (flightParam && todaysFlights.length > 0 && !selectedFlight) {
      const match = todaysFlights.find(f => f.flight_number === flightParam);
      if (match) setSelectedFlight(match);
    }
  }, [searchParams, todaysFlights, selectedFlight]);

  const handleFlightClick = useCallback((flight: Flight) => {
    setSelectedFlight(flight);
    setSearchParams({ flight: flight.flight_number }, { replace: true });
  }, [setSearchParams]);

  const handleClosePanel = useCallback(() => {
    setSelectedFlight(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const structuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "National Airports Corporation Flight Information System",
    "description": "Real-time flight tracking system for Papua New Guinea airports",
    "url": "https://pngflightradar.com",
    "applicationCategory": "Transportation",
    "operatingSystem": "Web Browser",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
    "featureList": [
      "Real-time flight tracking",
      "Airport departure boards",
      "Flight status updates",
      "PNG airport information"
    ]
  }), []);

  return (
    <>
      <SEOHead
        title="Find Your Flight in PNG with NAC, Runway to 50"
        description="Find Your Flight in PNG with NAC, Runway to 50"
        keywords="PNG flights, Papua New Guinea flights, flight tracker, airport departures, arrivals, flight status, PNG aviation, real-time flights"
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined}
        structuredData={structuredData}
      />

      <div className="min-h-screen" style={{ backgroundColor: "#0a0f2c" }}>
        <FIDSHeader title="All Flights" searchTerm={searchTerm} onSearchChange={setSearchTerm} />

        <div className="w-full">
          {isLoading ? (
            <div className="flex justify-center items-center py-12" style={{ minHeight: "400px" }}>
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#00c8d7" }} />
              <span className="ml-2 text-lg text-white">Loading flights...</span>
            </div>
          ) : (
            <FlightTabs
              todaysFlights={filteredFlights}
              allFlights={flights}
              searchTerm={searchTerm}
              onFlightClick={handleFlightClick}
            />
          )}

          <AnnouncementBanner />

          <div className="px-4 py-6 text-center text-white/70 [&_a]:text-[#00c8d7] [&_a:hover]:text-[#00c8d7]/80 [&_p]:text-white/70 [&_.border-t]:border-white/20">
            <OrganizationLinks />
          </div>
        </div>

        <FIDSMarquee />
      </div>

      <FlightDetailPanel flight={selectedFlight} onClose={handleClosePanel} />
    </>
  );
}
